import React, { useEffect, useMemo, useState } from "react";
import supabase from "../../../../conndb";
import NavabarGest from "./navabarGes";
import Loader from "./loader";

export default function Stor_interventi() {
  const [clienti, setClienti] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedClienteId, setSelectedClienteId] = useState(null);
  const [loading, setLoading] = useState(true);

  const norm = (v) => (v ?? "").toString().toLowerCase().trim();

  const toISODate = (d) => {
    if (!d) return "";
    const x = new Date(d);
    if (Number.isNaN(x.getTime())) return norm(d);
    const yyyy = x.getFullYear();
    const mm = String(x.getMonth() + 1).padStart(2, "0");
    const dd = String(x.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const toITDate = (d) => {
    if (!d) return "";
    const x = new Date(d);
    if (Number.isNaN(x.getTime())) return norm(d);
    return x.toLocaleDateString("it-IT");
  };

  const fetchClienti = async () => {
    setLoading(true);

    const { data: clientiData, error: errClienti } = await supabase.from("clienti").select("*");
    if (errClienti) {
      console.error(errClienti);
      setLoading(false);
      return;
    }

    const { data: interventiStufe, error: errStufe } = await supabase
      .from("interventi_stufe")
      .select("*, stufe(matricola, modello)")
      .order("data", { ascending: false });

    const { data: interventiCaldaie, error: errCaldaie } = await supabase
      .from("interventi_caldaie")
      .select("*, caldaie(matricola, modello)")
      .order("data", { ascending: false });

    if (errStufe || errCaldaie) {
      console.error(errStufe || errCaldaie);
      setLoading(false);
      return;
    }

    const clientiConInterventi = (clientiData || []).map((cliente) => {
      const stufe = (interventiStufe || [])
        .filter((i) => i.cliente_id === cliente.id)
        .map((i) => ({
          ...i,
          tipo: "stufa",
          modello: i.stufe?.modello || "N/A",
          matricola: i.stufe?.matricola || "N/A",
          dataISO: toISODate(i.data),
          dataIT: toITDate(i.data),
        }));

      const caldaie = (interventiCaldaie || [])
        .filter((i) => i.cliente_id === cliente.id)
        .map((i) => ({
          ...i,
          tipo: "caldaia",
          modello: i.caldaie?.modello || "N/A",
          matricola: i.caldaie?.matricola || "N/A",
          dataISO: toISODate(i.data),
          dataIT: toITDate(i.data),
        }));

      const interventi = [...stufe, ...caldaie].sort((a, b) => new Date(b.data) - new Date(a.data));

      return { ...cliente, interventi };
    });

    setClienti(clientiConInterventi);
    setLoading(false);
  };

  useEffect(() => {
    fetchClienti();
  }, []);

  const handleDeleteIntervento = async (intervento) => {
    const conferma = window.confirm(
      `Sei sicuro di voler eliminare l'intervento del ${intervento.dataISO || intervento.data}?`
    );
    if (!conferma) return;

    const table = intervento.tipo === "stufa" ? "interventi_stufe" : "interventi_caldaie";
    const { error } = await supabase.from(table).delete().eq("id", intervento.id);

    if (error) {
      console.error("Errore durante l'eliminazione:", error);
    } else {
      fetchClienti();
    }
  };

  const filteredClienti = useMemo(() => {
    const term = norm(search);
    if (!term) return clienti;

    return (clienti || []).filter((c) => {
      const baseHay = [
        c.nome,
        c.cognome,
        c.paese,
        c.indirizzo,
        c.telefono,
      ]
        .map(norm)
        .join(" ");

      if (baseHay.includes(term)) return true;

      // cerca anche negli interventi
      return (c.interventi || []).some((i) => {
        const hay = [
          i.tipo,
          i.dataISO,
          i.dataIT,
          i.modello,
          i.matricola,
          i.note,
        ]
          .map(norm)
          .join(" ");

        return hay.includes(term);
      });
    });
  }, [clienti, search]);

  // Se il cliente selezionato non è più nei risultati (perché la ricerca filtra), chiudo la vista
  useEffect(() => {
    if (!selectedClienteId) return;
    const stillVisible = filteredClienti.some((c) => c.id === selectedClienteId);
    if (!stillVisible) setSelectedClienteId(null);
  }, [filteredClienti, selectedClienteId]);

  const selectedCliente = useMemo(
    () => clienti.find((c) => c.id === selectedClienteId) || null,
    [clienti, selectedClienteId]
  );

  if (loading) return <Loader />;

  return (
    <div className="p-4 md:p-6 bg-[#173341] min-h-screen">
      <NavabarGest />

      <div className="bg-[#5b90ab70] p-4 md:p-8 rounded-2xl">
        <h1 className="text-2xl md:text-3xl text-white text-center font-bold mb-4">
          Storico Interventi
        </h1>

        <input
          type="text"
          placeholder="Cerca per cliente, data, modello, matricola, note..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-4 p-2 bg-[#173341] text-white rounded-2xl w-full outline-none"
        />

        {/* DESKTOP/TABLET */}
        <div className="hidden md:block">
          <div className="overflow-x-auto rounded-2xl">
            <table className="w-full text-white bg-[#173341] rounded-2xl overflow-hidden">
              <thead className="bg-[#0f2430]">
                <tr className="text-left">
                  <th className="p-3">Nome</th>
                  <th className="p-3">Cognome</th>
                  <th className="p-3">Telefono</th>
                  <th className="p-3">Indirizzo</th>
                  <th className="p-3">Paese</th>
                  <th className="p-3 text-center"># Interventi</th>
                  <th className="p-3 text-right">Azioni</th>
                </tr>
              </thead>

              <tbody>
                {filteredClienti.map((c) => (
                  <tr key={c.id} className="border-t border-white/10">
                    <td className="p-3">{c.nome}</td>
                    <td className="p-3">{c.cognome}</td>
                    <td className="p-3">{c.telefono}</td>
                    <td className="p-3 max-w-[360px] truncate" title={c.indirizzo || ""}>
                      {c.indirizzo}
                    </td>
                    <td className="p-3">{c.paese}</td>
                    <td className="p-3 text-center">{(c.interventi || []).length}</td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => setSelectedClienteId(c.id)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-xl"
                      >
                        Visualizza
                      </button>
                    </td>
                  </tr>
                ))}

                {!filteredClienti.length && (
                  <tr>
                    <td className="p-4 text-center text-white/80" colSpan={7}>
                      Nessun cliente trovato.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* MOBILE */}
        <div className="md:hidden space-y-3">
          {filteredClienti.map((c) => (
            <div
              key={c.id}
              className="bg-[#173341] rounded-2xl p-4 flex items-center justify-between"
            >
              <div className="text-white">
                <div className="font-bold">
                  {c.nome} {c.cognome}
                </div>
                <div className="text-xs text-white/70">
                  Interventi: {(c.interventi || []).length}
                </div>
              </div>

              <button
                onClick={() => setSelectedClienteId(c.id)}
                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-xl text-sm"
              >
                Visualizza
              </button>
            </div>
          ))}

          {!filteredClienti.length && (
            <div className="bg-[#173341] rounded-2xl p-4 text-white/80 text-center">
              Nessun cliente trovato.
            </div>
          )}
        </div>
      </div>

      {/* DETTAGLIO FULLSCREEN */}
      {selectedCliente && (
        <div className="fixed inset-0 z-50 bg-[#173341] overflow-y-auto">
          <div className="p-4 md:p-6">
            <NavabarGest />

            <div className="mt-4 bg-[#5b90ab70] rounded-2xl p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="text-white">
                  <h2 className="text-xl md:text-2xl font-bold">
                    Interventi: {selectedCliente.nome} {selectedCliente.cognome}
                  </h2>
                  <p className="text-white/80 text-sm md:text-base">
                    Lista interventi associati al cliente
                  </p>
                </div>

                <button
                  onClick={() => setSelectedClienteId(null)}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-xl px-3 py-2"
                  aria-label="Chiudi"
                  title="Chiudi"
                >
                  ✕
                </button>
              </div>

              {/* Dati cliente */}
              <div className="mt-4 bg-[#173341] rounded-2xl p-4">
                <h3 className="text-white font-bold mb-3">Dati cliente</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <InfoBox label="Nome" value={selectedCliente.nome} />
                  <InfoBox label="Cognome" value={selectedCliente.cognome} />
                  <InfoBox label="Telefono" value={selectedCliente.telefono} />
                  <InfoBox label="Paese" value={selectedCliente.paese} />
                  <div className="md:col-span-2">
                    <InfoBox label="Indirizzo" value={selectedCliente.indirizzo} />
                  </div>
                </div>
              </div>

              {/* Interventi */}
              <div className="mt-4 bg-[#173341] rounded-2xl p-4">
                <h3 className="text-white font-bold mb-3">
                  Interventi ({(selectedCliente.interventi || []).length})
                </h3>

                <div className="space-y-3">
                  {(selectedCliente.interventi || []).length === 0 && (
                    <div className="text-white/80">Nessun intervento per questo cliente.</div>
                  )}

                  {(selectedCliente.interventi || []).map((i) => (
                    <div
                      key={`${i.tipo}-${i.id}`}
                      className="bg-[#5b90ab70] rounded-2xl p-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3"
                    >
                      <div className="text-white text-sm md:text-base">
                        <p><strong>Tipo:</strong> {i.tipo}</p>
                        <p><strong>Data:</strong> {i.dataISO || i.data}</p>
                        <p className="break-words"><strong>Modello:</strong> {i.modello}</p>
                        <p className="break-words"><strong>Matricola:</strong> {i.matricola}</p>
                        <p className="break-words"><strong>Note:</strong> {i.note || "-"}</p>
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => handleDeleteIntervento(i)}
                          className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-2xl"
                        >
                          Elimina
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <button
                  onClick={() => setSelectedClienteId(null)}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-2xl font-semibold"
                >
                  Torna alla lista
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoBox({ label, value }) {
  return (
    <div className="p-3 bg-[#5b90ab70] rounded-2xl flex flex-col gap-1">
      <div className="text-white/90 text-xs font-semibold">{label}</div>
      <div className="text-white break-words">{value || "-"}</div>
    </div>
  );
}
